import { parseBoolean, parseNumber, parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'
import { RolodexEntryJson } from './rolodexEntryFields'

export enum ViewerMode {
  SELECTION = 'selection',
  POINTER_NAV = 'pointer-navigation',
  ENV_CONTROLS = 'environment-controls',
}

export interface GeckoSiteModelOrientation {
  rotation?: [number, number, number]
  position?: [number, number, number]
}

export interface GeckoPotreePointCloudSettings {
  initial_position: [number, number, number]
  initial_view_direction: [number, number, number]
  edl_strength?: number
  edl_radius?: number
  point_budget?: number
  point_size?: number
}

export interface GeckoPotreePointCloudHotkey {
  position: [number, number, number]
  lookAt: [number, number, number]
}

export interface GeckoPotreePointCloudHotkeys {
  [key: string]: GeckoPotreePointCloudHotkey
}

/**
 * An entry that holds a processed point cloud the and associated
 * metadata.
 */
export class GeckoPotreePointCloud extends GeckoEntryBase<
  {
    name?: string
    default_settings: GeckoPotreePointCloudSettings
    site_model_orientation?: GeckoSiteModelOrientation
    use_legacy_rotation?: boolean
    hotkeys?: GeckoPotreePointCloudHotkeys
    point_cloud_transform?: [
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
    ]
  },
  {
    site: string
    organization: string
  }
> {
  //----------------------------------------------------------------------------

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    const settingsJson = json.data
      .default_settings as GeckoPotreePointCloudSettings
    const orientationJson = json.data.site_model_orientation as
      | GeckoSiteModelOrientation
      | undefined

    super(
      json,
      {
        name: parseString(json.data?.name) ?? undefined,
        default_settings: {
          initial_position: settingsJson?.initial_position ?? [0, 0, 0],
          initial_view_direction: settingsJson?.initial_view_direction ?? [
            0, 0, 0,
          ],
          edl_strength: parseNumber(settingsJson?.edl_strength) ?? undefined,
          edl_radius: parseNumber(settingsJson?.edl_radius) ?? undefined,
          point_budget: parseNumber(settingsJson?.point_budget) ?? undefined,
          point_size: parseNumber(settingsJson?.point_size) ?? undefined,
        },
        site_model_orientation: orientationJson,
        use_legacy_rotation:
          parseBoolean(json.data?.use_legacy_rotation) ?? undefined,
        hotkeys: json.data?.hotkeys as GeckoPotreePointCloudHotkeys | undefined,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
      },
      {}
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A simple factory method which creates a point cloud from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoPotreePointCloud(json)
  }
}
